import React from 'react';
import { motion } from 'framer-motion';
import { experiences } from '../data/Experience';
import { useTheme } from '../contexts/ThemeContext';

function Experience() {
  const { isDark } = useTheme();

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className={`h-auto p-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto"
      >
        {/* Desktop & Tablet Timeline */}
        <div className="hidden md:block">
          <div className="relative">
            {/* Timeline line - Adjusted to match content height */}
            <div className="absolute left-1/2 h-[calc(100%-2rem)] w-0.5 top-4">
              <div className="h-full w-full bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-400 animate-pulse" />
            </div>
            
            <div className="flex flex-col space-y-24 pb-4">
              {experiences.map((exp, index) => (
                <motion.div
                  key={exp.id}
                  variants={itemVariants}
                  className="relative"
                >
                  <div className="grid grid-cols-[2.5fr,0.5fr,2.5fr] items-start">
                    {/* Left Column */}
                    <div className={`${index % 2 === 0 ? 'pr-4' : 'pl-4'}`}>
                      {index % 2 === 0 ? (
                        // Date, Skills, and Testimonial - Right aligned
                        <div>
                          <div className="text-right">
                            <span className={`inline-block px-4 py-1.5 rounded-full text-sm
                              ${isDark ? 'bg-gray-700' : 'bg-gray-100'} 
                              text-yellow-500 font-bold tracking-wide mb-3`}
                            >
                              {exp.duration}
                            </span>
                          </div>
                          <div className="flex flex-wrap justify-end gap-2 mb-6">
                            {exp.technologies.map((tech, i) => (
                              <span
                                key={i}
                                className={`text-xs px-2 py-1 rounded-full
                                  ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}
                                  hover:scale-105 transition-transform duration-200`}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>

                          {/* Testimonial */}
                          {exp.testimonial && (
                            <motion.div
                              variants={itemVariants}
                              className={`p-4 rounded-xl ${
                                isDark ? 'bg-gray-800/50' : 'bg-gray-100/50'
                              } backdrop-blur-sm mt-6`}
                            >
                              <p className="text-sm italic text-right mb-3">{exp.testimonial.text}</p>
                              <div className="flex items-center justify-end gap-3">
                                <div className="text-right">
                                  <h4 className="text-sm font-semibold">{exp.testimonial.name}</h4>
                                  <p className={`text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                    {exp.testimonial.position}
                                  </p>
                                </div>
                                <img
                                  src={exp.testimonial.avatar}
                                  alt={exp.testimonial.name}
                                  className="w-10 h-10 rounded-full"
                                />
                              </div>
                            </motion.div>
                          )}
                        </div>
                      ) : (
                        // Content Card
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className={`p-6 rounded-xl shadow-xl
                            ${isDark ? 'bg-gray-800' : 'bg-white'}
                            border-r-4 border-yellow-500
                            hover:shadow-2xl transition-shadow duration-300`}
                        >
                          <h3 className="text-xl font-bold mb-1 text-right">{exp.title}</h3>
                          <h4 className={`text-lg ${isDark ? 'text-yellow-400' : 'text-yellow-600'} mb-4 font-medium text-right`}>
                            {exp.company}
                          </h4>
                          <ul className="list-disc mr-4 mb-6 space-y-2 text-right">
                            {exp.description.map((item, i) => (
                              <li key={i} className="text-sm leading-relaxed">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </div>

                    {/* Timeline dot */}
                    <div className="flex justify-center relative -mt-2 ml-0.5">
                      <div className={`w-4 h-4 rounded-full ${isDark ? 'bg-yellow-400' : 'bg-yellow-500'} relative`}>
                        <div className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-75" />
                        <div className="absolute inset-0 rounded-full bg-yellow-400 animate-pulse opacity-75" />
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className={`${index % 2 === 0 ? 'pl-4' : 'pr-4'}`}>
                      {index % 2 === 0 ? (
                        // Content Card
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className={`p-6 rounded-xl shadow-xl
                            ${isDark ? 'bg-gray-800' : 'bg-white'}
                            border-l-4 border-yellow-500
                            hover:shadow-2xl transition-shadow duration-300`}
                        >
                          <h3 className="text-xl font-bold mb-1">{exp.title}</h3>
                          <h4 className={`text-lg ${isDark ? 'text-yellow-400' : 'text-yellow-600'} mb-4 font-medium`}>
                            {exp.company}
                          </h4>
                          <ul className="list-disc ml-4 mb-6 space-y-2">
                            {exp.description.map((item, i) => (
                              <li key={i} className="text-sm leading-relaxed">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      ) : (
                        // Date, Skills, and Testimonial - Left aligned
                        <div>
                          <div className="text-left">
                            <span className={`inline-block px-4 py-1.5 rounded-full text-sm
                              ${isDark ? 'bg-gray-700' : 'bg-gray-100'} 
                              text-yellow-500 font-bold tracking-wide mb-3`}
                            >
                              {exp.duration}
                            </span>
                          </div>
                          <div className="flex flex-wrap justify-start gap-2 mb-6">
                            {exp.technologies.map((tech, i) => (
                              <span
                                key={i}
                                className={`text-xs px-2 py-1 rounded-full
                                  ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}
                                  hover:scale-105 transition-transform duration-200`}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>

                          {/* Testimonial */}
                          {exp.testimonial && (
                            <motion.div
                              variants={itemVariants}
                              className={`p-4 rounded-xl ${
                                isDark ? 'bg-gray-800/50' : 'bg-gray-100/50'
                              } backdrop-blur-sm mt-6`}
                            >
                              <p className="text-sm italic mb-3">{exp.testimonial.text}</p>
                              <div className="flex items-center gap-3">
                                <img
                                  src={exp.testimonial.avatar}
                                  alt={exp.testimonial.name}
                                  className="w-10 h-10 rounded-full"
                                />
                                <div>
                                  <h4 className="text-sm font-semibold">{exp.testimonial.name}</h4>
                                  <p className={`text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                    {exp.testimonial.position}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="md:hidden">
          <div className="relative">
            {/* Vertical timeline line - Adjusted to match content height */}
            <div className="absolute h-[calc(100%-2rem)] w-0.5 left-6 top-4">
              <div className="h-full w-full bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-400 animate-pulse" />
            </div>

            <div className="space-y-16 pb-4">
              {experiences.map((exp) => (
                <motion.div
                  key={exp.id}
                  variants={itemVariants}
                  className="relative pl-16"
                >
                  {/* Timeline dot with ripple effect */}
                  <div className="absolute left-6 top-0 transform -translate-x-1/2">
                    <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-yellow-400' : 'bg-yellow-500'} relative`}>
                      <div className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-75" />
                      <div className="absolute inset-0 rounded-full bg-yellow-400 animate-pulse opacity-75" />
                    </div>
                  </div>

                  {/* Date badge */}
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm
                      ${isDark ? 'bg-gray-700' : 'bg-gray-100'} 
                      text-yellow-500 font-bold tracking-wide`}
                    >
                      {exp.duration}
                    </span>
                  </div>

                  {/* Content card */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`p-4 rounded-xl shadow-lg ${
                      isDark ? 'bg-gray-800' : 'bg-white'
                    } border-l-4 border-yellow-500 mt-6`}
                  >
                    <h3 className="text-lg font-bold mb-2">{exp.title}</h3>
                    <h4 className={`text-md ${isDark ? 'text-yellow-400' : 'text-yellow-600'} mb-2`}>
                      {exp.company}
                    </h4>
                    <ul className="list-disc list-inside mb-3 space-y-2">
                      {exp.description.map((item, i) => (
                        <li key={i} className="text-sm">
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {exp.technologies.map((tech, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-1 rounded-full ${
                            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Experience;